import type { EdgeBar } from '../types/chart'
import type { Period } from './resolution'
import { alignTimestamp } from './resolution'

/**
 * Aggregate `source` bars into buckets sized by `target.ms`. Each output bar:
 *   timestamp = bucket start (via `alignTimestamp(bar.timestamp, target)`)
 *   open      = first source bar's open in the bucket
 *   high      = max source high in the bucket
 *   low       = min source low in the bucket
 *   close     = last source bar's close in the bucket
 *   volume    = sum of source volumes
 *   turnover  = sum when present on EVERY input bar; undefined otherwise
 *
 * Empty buckets are not emitted (gap rendering is klinecharts' job). Source
 * bars are assumed sorted ascending — output preserves that order.
 *
 * Spec: docs/superpowers/specs/2026-05-07-chart-1m-downsample-design.md
 */
export function aggregateBars(source: EdgeBar[], target: Period): EdgeBar[] {
  if (source.length === 0) return []

  const out: EdgeBar[] = []
  let current: EdgeBar | null = null
  let currentTurnoverSum = 0
  let currentTurnoverComplete = true

  for (const src of source) {
    const bucket = alignTimestamp(src.timestamp, target)

    if (current === null || bucket !== current.timestamp) {
      // Close out the previous bucket before starting a new one.
      if (current !== null) {
        if (currentTurnoverComplete) current.turnover = currentTurnoverSum
        out.push(current)
      }
      current = {
        timestamp: bucket,
        open: src.open,
        high: src.high,
        low: src.low,
        close: src.close,
        volume: src.volume,
      }
      if (src.turnover != null) {
        currentTurnoverSum = src.turnover
        currentTurnoverComplete = true
      } else {
        currentTurnoverSum = 0
        currentTurnoverComplete = false
      }
      continue
    }

    // Same bucket — fold this source bar in.
    if (src.high > current.high) current.high = src.high
    if (src.low < current.low) current.low = src.low
    current.close = src.close
    current.volume += src.volume
    if (src.turnover != null && currentTurnoverComplete) {
      currentTurnoverSum += src.turnover
    } else {
      currentTurnoverComplete = false
    }
  }

  // Flush the last bucket.
  if (current !== null) {
    if (currentTurnoverComplete) current.turnover = currentTurnoverSum
    out.push(current)
  }

  return out
}
